import { DEFAULT_LOCALE } from '../common/constants';
import type { Form } from '../common/types/form';
import type { FieldId } from '../common/types/form/form-fields';
import type {
	FieldValues,
	FieldValuesPartial,
} from '../common/types/form/form-fields/field-values';
import { convertFieldValue } from './field-value/utils';
import { convertQuestions } from './question/utils';
import { convertSections } from './section/utils';
import type { ApiFieldDataResponse } from './types/field-data';
import type { ApiFormChoicesResponse } from './types/form-choices';
import type { FormAnswers, FormQuestions, ProformaForm } from './types/proforma-form';

export const convertJiraFieldErrors = (
	form: ProformaForm,
	jiraFieldErrors: { [jiraFieldKey: string]: string },
): { [fieldId: FieldId]: string } => {
	const jiraFieldKeyMap = Object.entries(form.design.questions).reduce(
		(jiraFieldKeys, [fieldId, field]) => {
			if (field.jiraField) {
				return jiraFieldKeys.set(field.jiraField, fieldId);
			}
			return jiraFieldKeys;
		},
		new Map<string, FieldId>(),
	);

	return Object.entries(jiraFieldErrors).reduce(
		(formFieldErrors, [jiraFieldKey, jiraFieldError]) => {
			const fieldId = jiraFieldKeyMap.get(jiraFieldKey);
			if (fieldId !== undefined) {
				return {
					...formFieldErrors,
					[fieldId]: jiraFieldError,
				};
			}
			return formFieldErrors;
		},
		{},
	);
};

/**
 * Converts a Proforma form to a Form object
 * NOTE: Async fields are handled by asyncLoaders provided! Here they will be an empty answer
 * @param form - The Proforma form to convert
 * @param fieldData - The field data to apply to the form (Jira field values), which takes precedence over form state (if any)
 * @param formChoices - The form choices to apply to the form (Jira field/data connection choices), which takes precedence over form design (if any)
 * @param shouldApplyDefaultValues - Whether to apply the default values to the form (form design default values)
 * @returns The converted Form object
 */
export const convertToForm = (
	form: ProformaForm,
	fieldData?: ApiFieldDataResponse,
	formChoices?: ApiFormChoicesResponse,
	shouldApplyDefaultValues?: boolean,
): Form => {
	const fields = convertQuestions(
		form.design.questions,
		form.state?.answers,
		form.state?.status,
		fieldData,
		formChoices,
		shouldApplyDefaultValues,
	);
	const sections = convertSections(
		form.design.sections,
		form.design.layout,
		form.design.conditions,
	);
	const id = form.uuid || form.id?.toString() || '';

	return {
		fields,
		sections,
		id,
	};
};

export const getLocale = (form: ProformaForm): string =>
	form.design.settings.translatedLocale ?? form.design.settings.primaryLocale ?? DEFAULT_LOCALE;

// deprecated
export const convertToFormAnswers = (
	fieldValues: FieldValues,
	formQuestions: FormQuestions,
): FormAnswers =>
	Object.entries(formQuestions).reduce(
		(answers, [fieldId, formQuestion]) => ({
			...answers,
			[fieldId]: convertFieldValue(fieldValues[fieldId], formQuestion),
		}),
		{},
	);

export const convertValuesToFormAnswers = (
	fieldValues: FieldValuesPartial,
	formQuestions: FormQuestions,
): FormAnswers =>
	Object.entries(formQuestions).reduce(
		(answers, [fieldId, formQuestion]) => ({
			...answers,
			[fieldId]: convertFieldValue(fieldValues[fieldId], formQuestion),
		}),
		{},
	);
